import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  useSelector,
} from 'react-redux';

import type {
  RootState,
} from '../../../app/store';

import {
  memberService,
} from '../services/memberService';

import type {
  MemberInfo,
  UserStatus,
} from '../types/member.types';

import {
  storeService,
} from '../../store/services/storeService';

import type {
  StoreListItem,
} from '../../store/types/store.types';

import {
  useModal,
} from '../../../shared/components/ModalProvider';


export type StatusFilter =
  | 'all'
  | Extract<
      UserStatus,
      | 'ACTIVE'
      | 'PENDING'
      | 'INACTIVE'
    >;


export type StoreFilter =
  number | 'all';


const ITEMS_PER_PAGE =
  10;


/**
 * =========================================================
 * 잠시 대기
 *
 * 일시적인 네트워크 오류 재시도용
 * =========================================================
 */
const delay = (
  ms: number,
): Promise<void> =>
  new Promise(
    (resolve) => {
      window.setTimeout(
        resolve,
        ms,
      );
    },
  );


export const useAdminMembers =
  () => {

    const modal =
      useModal();


    const authUser =
      useSelector(
        (
          state:
            RootState,
        ) =>
          state.auth.user,
      );


    const isAdmin =
      authUser?.role ===
      'ADMIN';


    const isSuperAdmin =
      authUser?.role ===
      'SUPERADMIN';


    const [
      users,
      setUsers,
    ] =
      useState<
        MemberInfo[]
      >([]);


    const [
      stores,
      setStores,
    ] =
      useState<
        StoreListItem[]
      >([]);


    const [
      loading,
      setLoading,
    ] =
      useState(
        true,
      );


    /**
     * =========================================================
     * 조회 실패 메시지
     *
     * 기존 데이터를 지우지 않고
     * 실패 여부만 따로 관리합니다.
     * =========================================================
     */
    const [
      errorMessage,
      setErrorMessage,
    ] =
      useState('');


    const [
      searchTerm,
      setSearchTerm,
    ] =
      useState('');


    const [
      statusFilter,
      setStatusFilter,
    ] =
      useState<
        StatusFilter
      >(
        'all',
      );


    const [
      storeFilter,
      setStoreFilter,
    ] =
      useState<
        StoreFilter
      >(
        'all',
      );


    const [
      currentPage,
      setCurrentPage,
    ] =
      useState(
        1,
      );


    /**
     * =========================================================
     * 중복 조회 방지
     *
     * React StrictMode 또는
     * 빠른 연속 호출로 loadData가
     * 동시에 여러 번 실행되는 것을 방지합니다.
     * =========================================================
     */
    const loadingRef =
      useRef(
        false,
      );


    /**
     * =========================================================
     * 회원 목록 조회
     *
     * 일시적 실패 시 1회 재시도
     * =========================================================
     */
    const fetchMembers =
      useCallback(
        async (): Promise<
          MemberInfo[]
        > => {

          try {

            return await memberService
              .getMembers();

          } catch (
            firstError
          ) {

            console.warn(
              '회원 목록 1차 조회 실패. 재시도합니다.',
              firstError,
            );


            /**
             * Axios 401 refresh 등이
             * 막 끝난 직후일 수도 있으므로
             * 짧게 기다린 뒤 한 번만 재시도
             */
            await delay(
              500,
            );


            return await memberService
              .getMembers();
          }
        },
        [],
      );


    /**
     * =========================================================
     * 회원 + 매장 조회
     *
     * 중요:
     *
     * 회원 API와 매장 API를
     * 서로 독립적으로 처리합니다.
     *
     * 매장 API가 실패했다고
     * 회원 목록까지 지우지 않습니다.
     * =========================================================
     */
    const loadData =
      useCallback(
        async (): Promise<void> => {

          /**
           * 이미 조회 중이면
           * 새로운 요청을 만들지 않음
           */
          if (
            loadingRef.current
          ) {
            return;
          }


          loadingRef.current =
            true;


          try {

            setLoading(
              true,
            );


            setErrorMessage(
              '',
            );


            /**
             * =================================================
             * 두 API는 병렬 호출하되
             * Promise.allSettled 사용
             *
             * 한 API 실패가
             * 다른 API 성공을 무효화하지 않음
             * =================================================
             */
            const [
              usersResult,
              storesResult,
            ] =
              await Promise.allSettled([
                fetchMembers(),

                storeService
                  .getStores(),
              ]);


            /**
             * =================================================
             * 회원 목록 처리
             * =================================================
             */
            if (
              usersResult.status ===
              'fulfilled'
            ) {

              const safeUsers =
                Array.isArray(
                  usersResult.value,
                )
                  ? usersResult.value
                  : [];


              const sortedUsers =
                [
                  ...safeUsers,
                ].sort(
                  (
                    firstUser,
                    secondUser,
                  ) => {

                    const firstTime =
                      new Date(
                        firstUser.createAt,
                      ).getTime();


                    const secondTime =
                      new Date(
                        secondUser.createAt,
                      ).getTime();


                    /**
                     * 날짜가 잘못된 값이어도
                     * 정렬 때문에 전체가 깨지지 않도록 처리
                     */
                    const safeFirstTime =
                      Number.isNaN(
                        firstTime,
                      )
                        ? 0
                        : firstTime;


                    const safeSecondTime =
                      Number.isNaN(
                        secondTime,
                      )
                        ? 0
                        : secondTime;


                    return (
                      safeSecondTime -
                      safeFirstTime
                    );
                  },
                );


              setUsers(
                sortedUsers,
              );

            } else {

              console.error(
                '회원 목록 조회 실패:',
                usersResult.reason,
              );


              /**
               * 중요:
               *
               * setUsers([]) 하지 않음.
               *
               * 이전 데이터가 있다면
               * 그대로 유지합니다.
               */
              setErrorMessage(
                '회원 목록을 불러오지 못했습니다.',
              );


              await modal.alert(
                '회원 정보를 불러오지 못했습니다. 잠시 후 다시 시도해주세요.',
              );
            }


            /**
             * =================================================
             * 매장 목록 처리
             * =================================================
             */
            if (
              storesResult.status ===
              'fulfilled'
            ) {

              const safeStores =
                Array.isArray(
                  storesResult.value,
                )
                  ? storesResult.value
                  : [];


              setStores(
                safeStores,
              );

            } else {

              /**
               * 매장 API 실패는
               * 회원 목록 전체 실패로 처리하지 않음
               */
              console.error(
                '매장 목록 조회 실패:',
                storesResult.reason,
              );
            }

          } finally {

            loadingRef.current =
              false;


            setLoading(
              false,
            );
          }
        },
        [
          fetchMembers,
          modal,
        ],
      );


    /**
     * =========================================================
     * 최초 데이터 로딩
     * =========================================================
     */
    useEffect(
      () => {

        void loadData();

      },
      [
        loadData,
      ],
    );


    /**
     * =========================================================
     * 권한별 기본 지점
     *
     * ADMIN
     * → 자기 소속 지점
     *
     * SUPERADMIN
     * → 전체 지점
     * =========================================================
     */
    useEffect(
      () => {

        if (
          isAdmin &&
          authUser?.storeId
        ) {

          setStoreFilter(
            authUser.storeId,
          );


          return;
        }


        if (
          isSuperAdmin
        ) {

          setStoreFilter(
            'all',
          );
        }

      },
      [
        isAdmin,
        isSuperAdmin,
        authUser?.storeId,
      ],
    );


    /**
     * =========================================================
     * 필터링
     * =========================================================
     */
    const filteredUsers =
      useMemo(
        () => {

          const normalizedSearch =
            searchTerm
              .trim()
              .toLowerCase();


          return users.filter(
            (
              user,
            ) => {

              const matchesSearch =
                !normalizedSearch ||
                [
                  user.userName,
                  user.userNickname,
                  user.phoneNumber,
                  user.userId,
                ].some(
                  (
                    value,
                  ) =>
                    value
                      ?.toLowerCase()
                      .includes(
                        normalizedSearch,
                      ),
                );


              const matchesStatus =
                statusFilter ===
                  'all' ||
                user.status ===
                  statusFilter;


              const matchesStore =
                storeFilter ===
                  'all' ||
                user.storeId ===
                  storeFilter;


              return (
                matchesSearch &&
                matchesStatus &&
                matchesStore
              );
            },
          );
        },
        [
          users,
          searchTerm,
          statusFilter,
          storeFilter,
        ],
      );


    /**
     * =========================================================
     * 페이지네이션
     * =========================================================
     */
    const paginatedUsers =
      useMemo(
        () => {

          const startIndex =
            (
              currentPage -
              1
            ) *
            ITEMS_PER_PAGE;


          return filteredUsers
            .slice(
              startIndex,
              startIndex +
                ITEMS_PER_PAGE,
            );
        },
        [
          filteredUsers,
          currentPage,
        ],
      );


    /**
     * =========================================================
     * 필터 변경 시 첫 페이지
     * =========================================================
     */
    useEffect(
      () => {

        setCurrentPage(
          1,
        );

      },
      [
        searchTerm,
        statusFilter,
        storeFilter,
      ],
    );


    /**
     * =========================================================
     * 현재 페이지 보정
     * =========================================================
     */
    useEffect(
      () => {

        const totalPages =
          Math.max(
            1,
            Math.ceil(
              filteredUsers.length /
                ITEMS_PER_PAGE,
            ),
          );


        if (
          currentPage >
          totalPages
        ) {

          setCurrentPage(
            totalPages,
          );
        }

      },
      [
        currentPage,
        filteredUsers.length,
      ],
    );


    /**
     * =========================================================
     * 회원 비활성화
     * =========================================================
     */
    const deactivateMember =
      async (
        userId: string,
      ): Promise<void> => {

        const confirmed =
          await modal.confirm(
            '이 회원을 비활성화하시겠습니까?',
          );


        if (
          !confirmed
        ) {
          return;
        }


        try {

          await memberService
            .deactivateMember(
              userId,
            );


          await modal.alert(
            '회원이 비활성화되었습니다.',
          );


          await loadData();

        } catch (
          error: unknown
        ) {

          console.error(
            '회원 비활성화 실패:',
            error,
          );


          await modal.alert(
            '비활성화에 실패했습니다. 다시 시도해주세요.',
          );
        }
      };


    /**
     * =========================================================
     * 회원 활성화
     * =========================================================
     */
    const activateMember =
      async (
        userId: string,
      ): Promise<void> => {

        const confirmed =
          await modal.confirm(
            '이 회원을 활성화하시겠습니까?',
          );


        if (
          !confirmed
        ) {
          return;
        }


        try {

          await memberService
            .activateMember(
              userId,
            );


          await modal.alert(
            '회원이 활성화되었습니다.',
          );


          await loadData();

        } catch (
          error: unknown
        ) {

          console.error(
            '회원 활성화 실패:',
            error,
          );


          await modal.alert(
            '활성화에 실패했습니다. 다시 시도해주세요.',
          );
        }
      };


    /**
     * =========================================================
     * 필터 활성 여부
     * =========================================================
     */
    const hasActiveFilter =
      Boolean(
        searchTerm.trim(),
      ) ||
      statusFilter !==
        'all' ||
      storeFilter !==
        'all';


    return {
      users,
      stores,

      loading,

      /**
       * 페이지에서 재조회 버튼을
       * 만들 때 사용할 수 있음
       */
      errorMessage,


      searchTerm,
      setSearchTerm,


      statusFilter,
      setStatusFilter,


      storeFilter,
      setStoreFilter,


      filteredUsers,
      paginatedUsers,


      currentPage,
      setCurrentPage,


      itemsPerPage:
        ITEMS_PER_PAGE,


      hasActiveFilter,


      activateMember,
      deactivateMember,


      reload:
        loadData,
    };
  };